import { Inject, Injectable } from '@angular/core';
import { catchError, filter, from, map, mergeMap, Observable, of, take, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Auth,
  AuthError,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getRedirectResult,
  GoogleAuthProvider,
  idToken,
  reauthenticateWithCredential,
  SAMLAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithRedirect,
  updatePassword,
  UserCredential,
  validatePassword
} from '@angular/fire/auth';
import { TrpcService } from './trpc.service';
import { UserResponseType } from 'portal-types';
import { TRPCClientError } from '@trpc/client';
import Rollbar from 'rollbar';
import { RollbarService } from './rollbar.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VerifyEmailModalComponent } from '../verify-email-modal/verify-email-modal.component';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  constructor(private fbAuth: Auth,
              private trpc: TrpcService,
              private ngbModal: NgbModal,
              private transloco: TranslocoService,
              @Inject(RollbarService) private rollbar: Rollbar) {

    this.transloco.langChanges$
      .pipe(
        tap(language => this.fbAuth.languageCode = language)
      )
      .subscribe();
  }

  getToken(): Observable<string | null> {
    return idToken(this.fbAuth);
  }

  private isError(res: Error | UserCredential): res is Error {
    // could also be AuthError or FirebaseError.
    return (res as Error).message !== undefined;
  }

  validatePasswordWithPolicy(password: string, email: string) {
    return from(validatePassword(this.fbAuth, password))
      .pipe(
        catchError(validationError => {
          const event = {
            description: 'invalid_password',
            isError: true,
            email,
            provider: 'password'
          };
          // logger result doesn't matter
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              map(() => of(validationError)),
              catchError(() => of(validationError)),
            );
        })
      );
  }

  ssoSignIn(): Observable<UserResponseType> {
    const isAbfasOrAbpmLocation = window.location.pathname.includes('/org/abfas/') ||
      window.location.pathname.includes('/org/abpm/');

    return this.maybeHandleSignInWithRedirect()
      .pipe(
        mergeMap(() => this.trpc.client.sso.getToken.query()),
        mergeMap(token => from(signInWithCustomToken(this.fbAuth, token))),
        mergeMap(userCredential => {
          if (!userCredential.user.emailVerified && !isAbfasOrAbpmLocation) {
            this.ngbModal.open(VerifyEmailModalComponent, {
              size: 'lg',
              centered: true,
              beforeDismiss: () => false
            });
            throw new Error(`Email is not verified`);
          }
          return from(this.trpc.client.users.get.query())
            .pipe(
              catchError(e => {
                if (e instanceof TRPCClientError && e.data.httpStatus === 401) {
                  // Firebase sign in is valid but user doesn't exist in platform
                  this.rollbar.error(`User ${userCredential.user.email} has firebase account but failed to find user`);
                  return this.ssoSignOut();
                }

                throw e;
              })
            )
        }),
        tap(() => this.monitorFirebaseAuthState())
      );
  }

  ssoSignOut(): Observable<never> {
    return from(this.trpc.client.sso.signOut.mutate())
      .pipe(
        tap(() => location.reload())
      );
  }

  // If it's sign in with redirect, it never returns
  private maybeHandleSignInWithRedirect(): Observable<unknown> {
    return from(getRedirectResult(this.fbAuth))
      .pipe(
        catchError((redirectAuthError: AuthError) => {
          const event = {
            description: 'failed_redirect_login',
            isError: true,
            email: redirectAuthError.customData.email,
            code: redirectAuthError.code
          };
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              map(() => redirectAuthError), 
              catchError(() => of(redirectAuthError))
            );
        }),
        tap(res => {
          if (res && this.isError(res)) {
            throw new Error((res as Error).message);
          }
        }),
        mergeMap(user => user ? this.signedIn(): of(null))
      );
  }

  signInWithGoogle(): Observable<unknown> {
    // We don't need to call signedIn since this causes redirect
    return from(signInWithRedirect(this.fbAuth, new GoogleAuthProvider()))
      .pipe(
         catchError((redirectAuthError: AuthError) => {
          const event = {
            description: 'failed_google_login',
            isError: true,
            email: redirectAuthError.customData.email,
            code: redirectAuthError.code,
            provider: 'google'
          };
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              map(() => redirectAuthError), 
              catchError(() => of(redirectAuthError))
            );
        }),
        tap(res => {
          if (this.isError(res)) {
            throw new Error((res as Error).message);
          }
        })     
      );
  }

  signInWithPassword(email: string, password: string): Observable<unknown> {
    return from(signInWithEmailAndPassword(this.fbAuth, email, password))
      .pipe(
        catchError(passwordAuthError => {
          // goes above signedIn() because there is another logger in buildswell sso.sign-in route, so not needed here
          const event = {
            description: 'failed_password_login',
            isError: true,
            email,
            provider: 'password'
          };
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              catchError(() => this.mapAuthErrors(passwordAuthError)),
              mergeMap(() => this.mapAuthErrors(passwordAuthError)) 
            );
        }),
        mergeMap(() => this.signedIn())
      );
  }
  private signedIn(): Observable<never> {
    return idToken(this.fbAuth)
      .pipe(
        take(1),
        mergeMap(token => this.trpc.client.sso.signIn.mutate(token!)),
        tap(() => location.reload())
      );
  }

  signUpWithPassword(organization: string, email: string, password: string) {
    return from(createUserWithEmailAndPassword(this.fbAuth, email, password))
      .pipe(
        // "On successful creation of the user account, this user will also be signed in to your application."
        mergeMap(() => from(sendEmailVerification(
          this.fbAuth.currentUser!,
          {url: `${environment.client}/org/${organization}/dashboard`,handleCodeInApp: false}))),
        mergeMap(() =>
          idToken(this.fbAuth)
            .pipe(
              mergeMap(token => from(this.trpc.client.users.signUp.mutate({ organization, token: token! }))
            )
          )),
        mergeMap(() => this.signedIn()),
        catchError((err: AuthError) => {
          const event = {
            description: 'failed_password_signup',
            isError: true,
            email,
            code: err.code,
            provider: 'password'
          };
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              catchError(() => this.mapAuthErrors(err)),
              mergeMap(() => this.mapAuthErrors(err))
            );
        })
      );
  }


  signInWithSaml(provider: string) {
    const samlProvider = new SAMLAuthProvider(provider);
    return from(signInWithRedirect(this.fbAuth, samlProvider))
      .pipe(
         catchError((redirectAuthError: AuthError) => {
          const event = {
            description: 'failed_saml_login',
            isError: true,
            email: redirectAuthError.customData.email,
            code: redirectAuthError.code,
            provider: 'saml'
          };
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              map(() => redirectAuthError), 
              catchError(() => of(redirectAuthError))
            );
        }),
        tap(res => {
          if (this.isError(res)) {
            throw new Error((res as Error).message);
          }
        })     
      );
  }

  isLoggedInWithSaml(): boolean {
    let isLoggedIn = false;

    from(getRedirectResult(this.fbAuth))
      .pipe(
        mergeMap(res => of(!!res)),
        catchError(() => {
          return of(false);
        }),
        tap(res => isLoggedIn = res)
      )
      .subscribe();

    return isLoggedIn;
  }

  private monitorFirebaseAuthState() {
    // idToken emits when:
    // 1. Pushed logout button
    // 2. refreshing token failed because sso.signOut is called on different device
    // Note 1: not authState but idToken method is used since unlike idToken/user, authState is not invoked when token refreshes
    // Note 2: in order for refresh to happen, you need to actively read user/id token somewhere (we do it in route guard)
    idToken(this.fbAuth)
      .pipe(
        filter(token => !token),
        tap(() => location.reload())
      )
      .subscribe();
  }

  sendEmailVerification(organization: string): Observable<string> {
    return from(sendEmailVerification(
      this.fbAuth.currentUser!,
      {url: `${environment.client}/org/${organization}/dashboard`,handleCodeInApp: false}))
      .pipe(
        catchError(err => {
          const event = {
            description: 'failed_sending_email_verification',
            isError: true,
            email: this.fbAuth.currentUser!.email!,
          };
          return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
            .pipe(
              map(() => of(err)), 
              catchError(() => of(err))
            );
        }),
        tap(res => {
          if (this.isError(res)) {
            throw new Error((res as Error).message);
          }
        }),
        map(() => this.fbAuth.currentUser!.email!)
      );
  }

  triggerPasswordResetEmail(email: string, location: string): Observable<void> {
    return from(sendPasswordResetEmail(
        this.fbAuth,
        email,
        {
          url: `${environment.client}/org/${location}/dashboard`,
          handleCodeInApp: false
        }
      ))
        .pipe(
          catchError(err => {
            const event = {
              description: 'failed_sending_password_reset',
              isError: true,
              email,
              provider: 'password'
            };
            return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
              .pipe(
                catchError(() => {throw new Error(err.message)}),
                tap(() => {throw new Error(err.message)})
              );
          }),
        );
  }

  changePassword(email: string, password: string): Observable<void> {
    return from(updatePassword(this.fbAuth.currentUser!, password))
      .pipe(
        catchError(err => {
            const event = {
              description: 'failed_changing_password',
              isError: true,
              email,
              provider: 'password'
            };
            return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
              .pipe(
                catchError(() => {throw new Error(err.message)}),
                tap(() => {throw new Error(err.message)})
              );
          })
      );
  }

  reauthenticateWithPassword(password: string, email: string): Observable<UserCredential> {
    const credential = EmailAuthProvider.credential(email, password);
    return from(reauthenticateWithCredential(this.fbAuth.currentUser!, credential))
      .pipe(
        catchError(err => {
            const event = {
              description: 'failed_password_reauth',
              isError: true,
              email,
              provider: 'password'
            };
            return from(this.trpc.client.log.mutate({type: 'auth', params: event}))
              .pipe(
                catchError(() => {throw new Error(err.message)}),
                tap(() => {throw new Error(err.message)})
              );
          })
      );
  }

  private mapAuthErrors(err: AuthError): Observable<never> {
    switch (err.code) {
      case 'auth/email-already-in-use':
        throw err;
      /** nbcrna: throw new Error('You cannot set a new password as this email is already in use. Please sign in
       * instead.'); **/
      case 'auth/password-does-not-meet-requirements': {
        let passwordErr = 'Password does not meet requirements.';

        const matchRequirements = err.message.match(/^Firebase: Missing password requirements: \[(.*)\]/);
        if (matchRequirements && matchRequirements.length > 1) {
          passwordErr = matchRequirements[1];
        }
        throw new Error(passwordErr);
      }
      default:
        throw new Error((err as AuthError).code.replace(/^auth\//, ''));
    }
  }
}
